import { _escapePdfName } from './../utils';
import { _PdfColorSpace } from '../enumerator';
export class _PdfStreamWriter {
    constructor(stream) {
        this._newLine = '\r\n';
        this._whiteSpace = ' ';
        this._stream = stream;
    }
    _writeOperator(value) {
        this._stream.write(value);
        this._stream.write(this._newLine);
    }
    _saveGraphicsState() {
        this._writeOperator('q');
    }
    _restoreGraphicsState() {
        this._writeOperator('Q');
    }
    _writeComment(comment) {
        if (comment && comment.length > 0) {
            this._writeOperator('% ' + comment);
        }
    }
    _setGraphicsState(value) {
        this._stream.write(`/${_escapePdfName(value.name)} `);
        this._writeOperator('gs');
    }
    _modifyCtm(matrix) {
        this._stream.write(`${matrix._toString()} `);
        this._writeOperator('cm');
    }
    _modifyTM(matrix) {
        this._stream.write(`${matrix._toString()} `);
        this._writeOperator('Tm');
    }
    _setColorSpace(value, arg2, arg3) {
        if (typeof value === 'string' && typeof arg2 === 'boolean') {
            this._stream.write(`/${value} `);
            this._writeOperator(arg2 ? 'CS' : 'cs');
        }
        else if (Array.isArray(value) && typeof arg2 === 'number' && typeof arg3 === 'boolean') {
            let colorSpaceName;
            switch (arg2) {
                case _PdfColorSpace.rgb:
                    colorSpaceName = 'DeviceRGB';
                    break;
                case _PdfColorSpace.cmyk:
                    colorSpaceName = 'DeviceCMYK';
                    break;
                case _PdfColorSpace.grayScale:
                    colorSpaceName = 'DeviceGray';
                    break;
                default:
                    colorSpaceName = 'DeviceRGB';
                    break;
            }
            this._stream.write(`/${colorSpaceName} `);
            this._writeOperator(arg3 ? 'CS' : 'cs');
            this._setColor(value, arg3);
        }
    }
    _setColor(color, forStroking) {
        this._stream.write(`${(color[0] / 255).toFixed(3)} ${(color[1] / 255).toFixed(3)} ${(color[2] / 255).toFixed(3)} `);
        this._writeOperator(forStroking ? 'RG' : 'rg');
    }
    _appendRectangle(x, y, width, height) {
        this._writePoint(x, y);
        this._writePoint(width, height);
        this._writeOperator('re');
    }
    _writePoint(x, y) {
        this._stream.write(`${x.toFixed(3)} ${(-y).toFixed(3)} `);
    }
    _clipPath(isEvenOdd) {
        this._stream.write(`${isEvenOdd ? 'W*' : 'W'} n${this._newLine}`);
    }
    _fillPath(isEvenOdd) {
        this._writeOperator(isEvenOdd ? 'f*' : 'f');
    }
    _closeFillPath(isEvenOdd) {
        this._writeOperator('h');
        this._fillPath(isEvenOdd);
    }
    _strokePath() {
        this._writeOperator('S');
    }
    _closeStrokePath() {
        this._writeOperator('s');
    }
    _fillStrokePath(isEvenOdd) {
        this._writeOperator(isEvenOdd ? 'B*' : 'B');
    }
    _closeFillStrokePath(isEvenOdd) {
        this._writeOperator(isEvenOdd ? 'b*' : 'b');
    }
    _endPath() {
        this._writeOperator('n');
    }
    _setFont(name, size) {
        this._stream.write(`/${name} ${size.toFixed(3)} `);
        this._writeOperator('Tf');
    }
    _setTextScaling(textScaling) {
        this._stream.write(`${textScaling.toFixed(3)} `);
        this._writeOperator('Tz');
    }
    _closePath() {
        this._writeOperator('h');
    }
    _startNextLine(x, y) {
        if (typeof x === 'undefined') {
            this._writeOperator('T*');
        }
        else {
            this._writePoint(x, y);
            this._writeOperator('Td');
        }
    }
    _setLeading(leading) {
        this._write(`${leading.toFixed(3)} `);
        this._write(this._whiteSpace);
        this._writeOperator('TL');
    }
    _showText(text) {
        this._writeText(text);
        this._writeOperator('Tj');
    }
    _write(string) {
        let builder = '';
        builder += string;
        builder += '\r\n';
        this._writeOperator(builder);
    }
    _writeText(text) {
        let result = '';
        const data = this._escapeSymbols(text);
        for (let i = 0; i < data.length; i++) {
            result += String.fromCharCode(data[i]); // eslint-disable-line
        }
        result = '(' + result + ')';
        this._stream.write(result);
    }
    _beginText() {
        this._writeOperator('BT');
    }
    _endText() {
        this._writeOperator('ET');
    }
    _beginPath(x, y) {
        this._writePoint(x, y);
        this._writeOperator('m');
    }
    _appendLineSegment(x, y) {
        this._writePoint(x, y);
        this._writeOperator('l');
    }
    _appendBezierSegment(x1, y1, x2, y2, x3, y3) {
        this._writePoint(x1, y1);
        this._writePoint(x2, y2);
        this._writePoint(x3, y3);
        this._writeOperator('c');
    }
    _setTextRenderingMode(renderingMode) {
        this._stream.write(`${renderingMode.toString()} `);
        this._writeOperator('Tr');
    }
    _setCharacterSpacing(charSpacing) {
        this._stream.write(`${charSpacing.toFixed(3)} `);
        this._writeOperator('Tc');
    }
    _setWordSpacing(wordSpacing) {
        this._stream.write(`${wordSpacing.toFixed(3)} `);
        this._writeOperator('Tw');
    }
    _showNextLineText(text, unicode) {
        if (unicode !== null && typeof unicode !== 'undefined' && unicode) {
            this._writeText(text);
            this._writeOperator('\'');
        }
        else {
            this._stream.write(text);
            this._writeOperator('\'');
        }
    }
    _setLineDashPattern(pattern, patternOffset) {
        let tempPattern = '[';
        if (pattern.length >= 1) {
            for (let index = 0; index < pattern.length; index++) {
                if (index === pattern.length - 1) {
                    tempPattern += pattern[index].toString(); // eslint-disable-line
                }
                else {
                    tempPattern += pattern[index].toString() + ' '; // eslint-disable-line
                }
            }
        }
        tempPattern += '] ';
        tempPattern += patternOffset.toString();
        tempPattern += ' d';
        this._writeOperator(tempPattern);
    }
    _setMiterLimit(miterLimit) {
        this._stream.write(`${miterLimit.toFixed(3)} `);
        this._writeOperator('M');
    }
    _setLineWidth(width) {
        this._stream.write(`${width.toFixed(3)} `);
        this._writeOperator('w');
    }
    _setLineCap(lineCapStyle) {
        this._stream.write(`${lineCapStyle} `);
        this._writeOperator('J');
    }
    _setLineJoin(lineJoinStyle) {
        this._stream.write(`${lineJoinStyle} `);
        this._writeOperator('j');
    }
    _executeObject(name) {
        this._stream.write(`/${name.name} `);
        this._writeOperator('Do');
    }
    _beginMarkupSequence(name) {
        this._stream.write(`/${name} `);
        this._writeOperator('BMC');
    }
    _endMarkupSequence() {
        this._writeOperator('EMC');
    }
    _clear() {
        this._stream._bytes = [];
    }
    _escapeSymbols(value) {
        const data = [];
        for (let i = 0; i < value.length; i++) {
            const currentData = value.charCodeAt(i);
            switch (currentData) {
                case 40:
                case 41:
                    data.push(92);
                    data.push(currentData);
                    break;
                case 13:
                    data.push(92);
                    data.push(114);
                    break;
                case 92:
                    data.push(92);
                    data.push(currentData);
                    break;
                default:
                    data.push(currentData);
                    break;
            }
        }
        return data;
    }
}
